import { Button } from 'components/Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/Card/Card';
import { Input } from 'components/Input/Input';
import { Label } from 'components/Label/Label';
import { useToast } from 'components/Toast/use-toast';
import { Typography } from 'components/Typograhpy/Typography';
import { cn } from 'lib/utils';
import { Check, Copy, KeyRound, Loader2, RefreshCw, Shield, Smartphone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import {
    get2FASettings,
    setupTotp,
    verifyTotpSetup,
    setPreferred2FAMethod,
    disableTotp,
    regenerateBackupCodes,
} from 'services/auth';

type TwoFASettings = {
    preferredMethod: 'sms' | 'totp';
    totpEnabled: boolean;
    backupCodesRemaining: number;
    lastUsedAt: string | null;
};

type SetupStep = 'idle' | 'qr-code' | 'verify' | 'backup-codes' | 'complete';

const getTotpActionButton = (
    settings: TwoFASettings | null,
    startTotpSetup: () => void,
    handleSetPreferredMethod: (method: 'sms' | 'totp') => void
) => {
    if (!settings?.totpEnabled) {
        return (
            <Button onClick={startTotpSetup}>
                Set Up
            </Button>
        );
    }
    if (settings?.preferredMethod !== 'totp') {
        return (
            <Button variant="outline" onClick={() => handleSetPreferredMethod('totp')}>
                Use TOTP
            </Button>
        );
    }
    return <div className="text-sm text-green-600 font-medium">Current method</div>;
};

export const TwoFactorSettings = () => {
    const [settings, setSettings] = useState<TwoFASettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [setupStep, setSetupStep] = useState<SetupStep>('idle');
    const [totpSecret, setTotpSecret] = useState('');
    const [provisioningUri, setProvisioningUri] = useState('');
    const [verifyCode, setVerifyCode] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [disableCode, setDisableCode] = useState('');
    const [showDisableModal, setShowDisableModal] = useState(false);
    const { toast } = useToast();

    const loadSettings = async () => {
        setIsLoading(true);
        try {
            const result: any = await get2FASettings();
            if (result.data?.get2FaSettings?.settings) {
                setSettings(result.data.get2FaSettings.settings);
            }
        } catch (e) {
            console.error(e);
            toast({
                description: 'Failed to load 2FA settings',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startTotpSetup = async () => {
        setIsLoading(true);
        try {
            const result: any = await setupTotp();
            if (result.errors?.length > 0) {
                toast({
                    description: result.errors[0].message,
                    variant: 'destructive',
                });
                return;
            }
            if (result.data?.setupTotp) {
                setTotpSecret(result.data.setupTotp.secret);
                setProvisioningUri(result.data.setupTotp.provisioningUri);
                setSetupStep('qr-code');
            }
        } catch (e) {
            console.error(e);
            toast({
                description: 'Failed to initialize TOTP setup',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const verifyTotpCode = async () => {
        setIsLoading(true);
        try {
            const result: any = await verifyTotpSetup(verifyCode);
            if (result.errors?.length > 0) {
                toast({
                    description: result.errors[0].message,
                    variant: 'destructive',
                });
                return;
            }
            if (result.data?.verifyTotpSetup?.success) {
                setBackupCodes(result.data.verifyTotpSetup.backupCodes);
                setSetupStep('backup-codes');
                toast({
                    description: 'TOTP enabled successfully!',
                    variant: 'default',
                });
            }
        } catch (e) {
            console.error(e);
            toast({
                description: 'Invalid code. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const completeSetup = () => {
        setSetupStep('idle');
        setBackupCodes([]);
        setVerifyCode('');
        loadSettings();
    };

    const handleSetPreferredMethod = async (method: 'sms' | 'totp') => {
        setIsLoading(true);
        try {
            const result: any = await setPreferred2FAMethod(method);
            if (result.errors?.length > 0) {
                toast({
                    description: result.errors[0].message,
                    variant: 'destructive',
                });
                return;
            }
            if (result.data?.setPreferred2FaMethod?.success) {
                toast({
                    description: `Preferred 2FA method set to ${method === 'totp' ? 'Authenticator App' : 'SMS'}`,
                    variant: 'default',
                });
                loadSettings();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisableTotp = async () => {
        setIsLoading(true);
        try {
            const result: any = await disableTotp(disableCode);
            if (result.errors?.length > 0) {
                toast({
                    description: result.errors[0].message,
                    variant: 'destructive',
                });
                return;
            }
            if (result.data?.disableTotp?.success) {
                toast({
                    description: 'TOTP has been disabled. You will now use SMS for 2FA.',
                    variant: 'default',
                });
                setShowDisableModal(false);
                setDisableCode('');
                loadSettings();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegenerateBackupCodes = async () => {
        setIsLoading(true);
        try {
            const result: any = await regenerateBackupCodes();
            if (result.errors?.length > 0) {
                toast({
                    description: result.errors[0].message,
                    variant: 'destructive',
                });
                return;
            }
            if (result.data?.regenerateBackupCodes?.success) {
                setBackupCodes(result.data.regenerateBackupCodes.backupCodes);
                setSetupStep('backup-codes');
                toast({
                    description: 'New backup codes generated. Save them securely!',
                    variant: 'default',
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            description: 'Copied to clipboard',
            variant: 'default',
        });
    };

    if (isLoading && !settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
        );
    }

    // QR Code Setup Step
    if (setupStep === 'qr-code') {
        return (
            <div className="max-w-md mx-auto">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5" />
                            Set Up Authenticator App
                        </CardTitle>
                        <CardDescription>
                            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-6">
                        <div className="bg-white p-4 rounded-lg flex justify-center">
                            <QRCode value={provisioningUri} size={200} />
                        </div>
                        <div>
                            <Label className="text-zinc-500 text-sm">Manual entry code:</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <code className="bg-zinc-100 px-3 py-2 rounded font-mono text-sm flex-1">
                                    {totpSecret}
                                </code>
                                <Button variant="outline" size="sm" onClick={() => copyToClipboard(totpSecret)}>
                                    <Copy className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <Button variant="outline" onClick={() => setSetupStep('idle')}>
                                Cancel
                            </Button>
                            <Button onClick={() => setSetupStep('verify')}>Continue</Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Verify Code Step
    if (setupStep === 'verify') {
        return (
            <div className="max-w-md mx-auto">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Verify Setup
                        </CardTitle>
                        <CardDescription>
                            Enter the 6-digit code from your authenticator app to verify the setup.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-6">
                        <div>
                            <Label>Verification Code</Label>
                            <Input
                                type="text"
                                placeholder="000000"
                                maxLength={6}
                                value={verifyCode}
                                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                                className="text-center text-2xl tracking-widest font-mono"
                            />
                        </div>
                        <div className="flex justify-between">
                            <Button variant="outline" onClick={() => setSetupStep('qr-code')}>
                                Back
                            </Button>
                            <Button onClick={verifyTotpCode} disabled={verifyCode.length !== 6 || isLoading}>
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Verify
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Backup Codes Step
    if (setupStep === 'backup-codes') {
        return (
            <div className="max-w-md mx-auto">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-green-600">
                            <Check className="w-5 h-5" />
                            Save Your Backup Codes
                        </CardTitle>
                        <CardDescription>
                            Store these codes securely. Each code can only be used once. You will need them if you lose
                            access to your authenticator app.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-6">
                        <div className="bg-zinc-50 p-4 rounded-lg grid grid-cols-2 gap-2">
                            {backupCodes.map((code, index) => (
                                <code key={index} className="bg-white px-3 py-2 rounded font-mono text-sm text-center">
                                    {code}
                                </code>
                            ))}
                        </div>
                        <Button variant="outline" onClick={() => copyToClipboard(backupCodes.join('\n'))}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy All Codes
                        </Button>
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                            <strong>Important:</strong> These codes will only be shown once. Make sure to save them in a
                            secure location.
                        </div>
                        <Button onClick={completeSetup}>I have saved these codes</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Disable TOTP Modal
    if (showDisableModal) {
        return (
            <div className="max-w-md mx-auto">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-orange-600">
                            <X className="w-5 h-5" />
                            Disable Authenticator App
                        </CardTitle>
                        <CardDescription>
                            Enter your current authenticator code or a backup code to disable TOTP. You will use SMS for
                            2FA after this.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-6">
                        <div>
                            <Label>Code</Label>
                            <Input
                                type="text"
                                placeholder="Enter code"
                                value={disableCode}
                                onChange={e => setDisableCode(e.target.value)}
                                className="text-center font-mono"
                            />
                        </div>
                        <div className="flex justify-between">
                            <Button variant="outline" onClick={() => setShowDisableModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleDisableTotp} disabled={!disableCode || isLoading}>
                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Disable TOTP
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Main Settings View
    return (
        <div className="max-w-2xl mx-auto p-6">
            <div className="mb-8">
                <Typography.H2>Two-Factor Authentication</Typography.H2>
                <p className="text-zinc-500 mt-2">
                    Secure your account with an additional layer of protection.
                </p>
            </div>

            <div className="space-y-6">
                {/* Current Status */}
                <Card>
                    <CardHeader>
                        <CardTitle>Current 2FA Method</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {settings?.preferredMethod === 'totp' ? (
                                    <>
                                        <KeyRound className="w-8 h-8 text-green-600" />
                                        <div>
                                            <div className="font-medium">Authenticator App (TOTP)</div>
                                            <div className="text-sm text-zinc-500">
                                                {settings?.backupCodesRemaining} backup codes remaining
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Smartphone className="w-8 h-8 text-blue-600" />
                                        <div>
                                            <div className="font-medium">SMS Verification</div>
                                            <div className="text-sm text-zinc-500">
                                                A code will be sent to your phone
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                <Check className="w-4 h-4" />
                                Active
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* SMS Option */}
                <Card className={cn(settings?.preferredMethod === 'sms' && 'border-blue-300 bg-blue-50/50')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Smartphone className="w-6 h-6 text-blue-600" />
                                <div>
                                    <div className="font-medium">SMS Verification</div>
                                    <div className="text-sm text-zinc-500">
                                        Receive a code via SMS to your registered phone number
                                    </div>
                                </div>
                            </div>
                            {settings?.preferredMethod !== 'sms' && settings?.totpEnabled && (
                                <Button variant="outline" onClick={() => handleSetPreferredMethod('sms')}>
                                    Use SMS
                                </Button>
                            )}
                            {settings?.preferredMethod === 'sms' && (
                                <div className="text-sm text-blue-600 font-medium">Current method</div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* TOTP Option */}
                <Card className={cn(settings?.preferredMethod === 'totp' && 'border-green-300 bg-green-50/50')}>
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <KeyRound className="w-6 h-6 text-green-600" />
                                <div>
                                    <div className="font-medium">Authenticator App (TOTP)</div>
                                    <div className="text-sm text-zinc-500">
                                        Use Google Authenticator, Authy, or similar apps
                                    </div>
                                </div>
                            </div>
                            {getTotpActionButton(settings, startTotpSetup, handleSetPreferredMethod)}
                        </div>
                    </CardContent>
                </Card>

                {/* TOTP Management (if enabled) */}
                {settings?.totpEnabled && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Authenticator App Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between py-2 border-b">
                                <div>
                                    <div className="font-medium">Backup Codes</div>
                                    <div className="text-sm text-zinc-500">
                                        {settings.backupCodesRemaining} codes remaining
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" onClick={handleRegenerateBackupCodes}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Regenerate
                                </Button>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium text-red-600">Disable Authenticator</div>
                                    <div className="text-sm text-zinc-500">
                                        Revert to SMS verification only
                                    </div>
                                </div>
                                <Button variant="destructive" size="sm" onClick={() => setShowDisableModal(true)}>
                                    Disable
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};
